// Clusters API service.
//
// Reads the SQL Server Always On availability group endpoints and maps them to
// the view models the Clusters screens render. The archiver returns SQL Server's
// own numeric state codes; the translation to readable strings lives here so the
// components stay presentational.

import api from './Base';
import archiver from './Archiver';
import { TimeRange } from '../types/TimeRange';
import { buildApiQuery } from '../helpers/timeRangeUtils';
import { showError } from '../utils/showAlert';
import { toEventsDataTable } from '../helpers/eventsDataTable';

import Cluster from '../types/cluster/Cluster';
import ClusterNode from '../types/cluster/ClusterNode';
import AvailabilityGroup from '../types/cluster/AvailabilityGroup';
import AvailabilityReplica from '../types/cluster/AvailabilityReplica';
import AvailabilityDatabase from '../types/cluster/AvailabilityDatabase';
import AvailabilityListener from '../types/cluster/AvailabilityListener';
import EventsDataTable from '../types/instance/tables/EventsDataTable';
import InstanceAvailabilityGroup from '../types/cluster/InstanceAvailabilityGroup';

/* ---- SQL Server state codes -------------------------------------------- */
// 0 is always the worst value, which is why the rollups take min().
const ROLE: Record<number, string> = { 0: 'RESOLVING', 1: 'PRIMARY', 2: 'SECONDARY' };
const HEALTH: Record<number, string> = { 0: 'NOT_HEALTHY', 1: 'PARTIALLY_HEALTHY', 2: 'HEALTHY' };
const SYNC_STATE: Record<number, string> = {
    0: 'NOT SYNCHRONIZING', 1: 'SYNCHRONIZING', 2: 'SYNCHRONIZED', 3: 'REVERTING', 4: 'INITIALIZING',
};
const OPERATIONAL: Record<number, string> = {
    0: 'PENDING_FAILOVER', 1: 'PENDING', 2: 'ONLINE', 3: 'OFFLINE', 4: 'FAILED', 5: 'FAILED_NO_QUORUM',
};

const code = (map: Record<number, string>, value: number | null | undefined, fallback = 'UNKNOWN'): string =>
    value === null || value === undefined ? fallback : (map[value] ?? fallback);

const flag = (value: number | null | undefined): boolean => value === 1;

/* ---- Raw response shapes ----------------------------------------------- */
interface RawReplica {
    id: number; servername: string; instanceid: number | null;
    availabilitymode: string; failovermode: string; readablesecondary: string;
    backuppriority: number; seedingmode: string;
    role: number; connectedstate: number; synchronizationhealth: number;
    operationalstate: number | null; timeslice: string; instancename: string | null;
    flowcontrolms: number | null;
}
interface RawListener { dnsname: string; port: number; ipaddresses: string; state: number | null }
interface RawGroup {
    id: number; name: string; guid: string; basicfeatures: boolean; isdistributed: boolean;
    replicas: RawReplica[]; listeners: RawListener[];
}
interface RawNode {
    name: string; membertype: string; state: string; quorumvotes: number;
    hostid: number | null; hostname: string | null;
}
interface RawCluster {
    id: number; name: string; type: string; quorumtype: string; quorumstate: string;
    nodes: RawNode[]; groups: RawGroup[];
}
interface RawSummary {
    groupid: number; groupname: string; clusterid: number; clustername: string;
    agreplicasnothealthy: number; agdatabasesnothealthy: number;
    aglisteners: number; agnotfailoverready: number;
    // fresh: some instance reported the group's replicas in the last five
    // minutes. lastsample: when one last did, in epoch milliseconds.
    fresh: boolean; lastsample: number | null;
}
interface RawDatabase {
    groupid: number; replicaid: number; servername: string; databasename: string;
    synchronizationstate: number; synchronizationhealth: number;
    issuspended: number; failoverready: number;
    logsendqueuekb: number | null; logsendratekb: number | null;
    redoqueuekb: number | null; redoratekb: number | null;
    secondarylagseconds: number | null; timeslice: string;
    ratesource: string | null;
}
interface RawTimePoint {
    timeslice: string; replicaid: number; databasename: string;
    logsendratekb: number | null; redoratekb: number | null;
}

/* ---- Fetch helpers ------------------------------------------------------ */
const get = async <T>(url: string, label: string): Promise<T[]> => {
    let result: T[] = [];
    await api.get<T[]>(url)
        .then((response) => { result = response.data; })
        .catch((error: any) => {
            console.error(`Failed to retrieve ${label}.`, error);
            showError(`Failed to retrieve ${label}`, error.message || error.toString());
        });
    return result;
};

// A screen loads clusters, nodes, groups and listeners together, and each of
// those comes from the same topology document. Holding the in-flight promise
// briefly turns four identical requests into one without risking stale reads.
const TOPOLOGY_TTL_MS = 3000;
let topologyCache: { at: number, promise: Promise<RawCluster[]> } | null = null;

const topology = (): Promise<RawCluster[]> => {
    const now = Date.now();
    if (!topologyCache || now - topologyCache.at > TOPOLOGY_TTL_MS) {
        topologyCache = { at: now, promise: get<RawCluster>('availability/group', 'cluster topology') };
    }
    return topologyCache.promise;
};

const summaries = (): Promise<RawSummary[]> =>
    get<RawSummary>('availability/state/summary', 'availability group summary');

const findGroup = (clusters: RawCluster[], groupId: number): { cluster: RawCluster, group: RawGroup } | undefined => {
    for (const cluster of clusters) {
        const group = cluster.groups.find(item => item.id === groupId);
        if (group) return { cluster, group };
    }
    return undefined;
};

// The stored states are the last ones sampled, however old. Once no instance
// has reported a group for a while -- the agent cannot reach either replica --
// they describe the past, so the live states read as unknown instead. Any one
// instance sees every replica in the group, so losing just one keeps it fresh.
// No summary at all is a failed request, not stale data, and is left alone.
const isStale = (summary: RawSummary | undefined): boolean =>
    summary !== undefined && !summary.fresh;

// Group health is a rollup, not a stored column: a group is unhealthy if any of
// its replicas or databases is. The summary endpoint computes both counts.
const groupHealth = (summary: RawSummary | undefined): string => {
    if (!summary || isStale(summary)) return 'UNKNOWN';
    return summary.agreplicasnothealthy > 0 || summary.agdatabasesnothealthy > 0
        ? 'NOT_HEALTHY' : 'HEALTHY';
};

const toGroup = (cluster: RawCluster, group: RawGroup, summary: RawSummary | undefined): AvailabilityGroup => {
    const primary = group.replicas.find(replica => replica.role === 1);
    const secondaries = group.replicas.filter(replica => replica.role !== 1);
    // Worst synchronisation state across the secondaries is what the group shows.
    const worstSync = secondaries.length === 0
        ? 2
        : Math.min(...secondaries.map(replica => replica.synchronizationhealth ?? 0));
    const flowControl = secondaries
        .map(replica => replica.flowcontrolms)
        .filter((value): value is number => value !== null && value !== undefined);
    const stale = isStale(summary);
    return {
        id: group.id,
        clusterid: cluster.id,
        name: group.name,
        groupid: group.guid,
        isbasic: group.basicfeatures,
        health: groupHealth(summary),
        syncstate: stale ? 'UNKNOWN' : worstSync === 2 ? 'SYNCHRONIZED' : 'NOT SYNCHRONIZING',
        failovermode: primary?.failovermode ?? 'MANUAL',
        failoverready: stale ? null : (summary ? summary.agnotfailoverready === 0 : false),
        primaryreplica: primary?.servername ?? '',
        // Flow control is the primary being throttled sending to a secondary,
        // so SQL Server records it against each secondary and never against
        // the primary's own row. Reading the primary's therefore always found
        // null. The group shows the worst link.
        flowcontrolms: stale || flowControl.length === 0
            ? undefined : Math.max(...flowControl),
        stale,
        lastsample: summary?.lastsample == null ? undefined : Number(summary.lastsample),
    };
};

const toReplica = (groupId: number, replica: RawReplica, failoverReady: boolean | null,
    stale: boolean): AvailabilityReplica => ({
    id: replica.id,
    groupid: groupId,
    servername: replica.servername,
    instanceid: replica.instanceid ?? null,
    role: code(ROLE, replica.role),
    availabilitymode: replica.availabilitymode,
    failovermode: replica.failovermode,
    readablesecondary: replica.readablesecondary,
    backuppriority: replica.backuppriority,
    seedingmode: replica.seedingmode,
    connectedstate: stale ? 'UNKNOWN' : (replica.connectedstate === 1 ? 'CONNECTED' : 'DISCONNECTED'),
    synchronizationhealth: stale ? 'UNKNOWN' : code(HEALTH, replica.synchronizationhealth),
    operationalstate: stale ? '' : code(OPERATIONAL, replica.operationalstate, ''),
    failoverready: stale ? null : failoverReady,
    flowcontrolms: stale ? null : replica.flowcontrolms ?? null,
});

/* ---- API ---------------------------------------------------------------- */
const ClusterApi = {
    getClusters: async (): Promise<Cluster[]> => {
        const [clusters, summary] = await Promise.all([topology(), summaries()]);
        return clusters.map(cluster => {
            const groups = summary.filter(item => item.clusterid === cluster.id)
                .filter(item => !isStale(item));
            const unhealthy = groups.some(item =>
                item.agreplicasnothealthy > 0 || item.agdatabasesnothealthy > 0);
            return {
                id: cluster.id,
                name: cluster.name,
                type: cluster.type,
                quorumtype: cluster.quorumtype,
                quorumstate: cluster.quorumstate,
                health: groups.length === 0 ? 'UNKNOWN' : (unhealthy ? 'NOT_HEALTHY' : 'HEALTHY'),
            };
        });
    },

    // Topology only — the menu badge needs no health rollup.
    getClusterCount: async (): Promise<number> => (await topology()).length,

    getCluster: async (clusterId: number): Promise<Cluster | undefined> =>
        (await ClusterApi.getClusters()).find(cluster => cluster.id === clusterId),

    getNodes: async (clusterId: number): Promise<ClusterNode[]> => {
        const [clusters, summary] = await Promise.all([topology(), summaries()]);
        const cluster = clusters.find(item => item.id === clusterId);
        if (!cluster) return [];
        // Node state comes from inventory and carries no time of its own, so it
        // is only as current as the cluster's groups.
        const groups = summary.filter(item => item.clusterid === clusterId);
        const stale = groups.length > 0 && groups.every(isStale);
        // Resource groups are not collected; the node's roles come from the
        // groups whose primary currently sits on it.
        return cluster.nodes.map((node, index) => ({
            id: index + 1,
            clusterid: cluster.id,
            name: node.name,
            hostid: node.hostid ?? null,
            state: stale ? 'UNKNOWN' : node.state,
            resourcegroups: cluster.groups
                .filter(group => group.replicas.some(replica =>
                    replica.role === 1 && replica.servername === node.name))
                .map(group => group.name),
        }));
    },

    getAvailabilityGroups: async (clusterId?: number): Promise<AvailabilityGroup[]> => {
        const [clusters, summary] = await Promise.all([topology(), summaries()]);
        return clusters
            .filter(cluster => clusterId === undefined || cluster.id === clusterId)
            .flatMap(cluster => cluster.groups.map(group =>
                toGroup(cluster, group, summary.find(item => item.groupid === group.id))));
    },

    getReplicas: async (groupId: number): Promise<AvailabilityReplica[]> => {
        const [clusters, summary] = await Promise.all([topology(), summaries()]);
        const found = findGroup(clusters, groupId);
        if (!found) return [];
        const groupSummary = summary.find(item => item.groupid === groupId);
        const ready = (groupSummary?.agnotfailoverready ?? 1) === 0;
        const stale = isStale(groupSummary);
        return found.group.replicas.map(replica =>
            toReplica(groupId, replica, replica.role === 1 ? true : ready, stale));
    },

    // Which availability groups a monitored instance replicates, and the role it
    // holds in each. Nothing server side maps a datasource to its groups, but the
    // topology already carries both halves of the join on the replica rows, so one
    // pass over it answers the question without a per-group fan-out.
    getInstanceMembership: async (instanceId: number): Promise<InstanceAvailabilityGroup[]> => {
        const clusters = await topology();
        return clusters.flatMap(cluster => cluster.groups
            .map(group => ({
                group,
                replica: group.replicas.find(replica => replica.instanceid === instanceId),
            }))
            .filter((item): item is { group: RawGroup, replica: RawReplica } => item.replica !== undefined)
            .map(({ group, replica }) => ({
                clusterid: cluster.id,
                groupid: group.id,
                groupname: group.name,
                role: code(ROLE, replica.role),
            })));
    },

    getDatabases: async (groupId: number, timeRange?: TimeRange): Promise<AvailabilityDatabase[]> => {
        const [rows, summary] = await Promise.all([
            get<RawDatabase>(`availability/database?group=${groupId}`, 'availability group databases'),
            summaries(),
        ]);
        const stale = isStale(summary.find(item => item.groupid === groupId));

        // Sparkline series, keyed by replica and database.
        const series = new Map<string, { send: number[], redo: number[] }>();
        if (timeRange) {
            const points = await get<RawTimePoint>(
                `availability/state/time?${buildApiQuery(timeRange)}`,
                'availability group trends');
            points.forEach(point => {
                const key = `${point.replicaid}|${point.databasename}`;
                const entry = series.get(key) ?? { send: [], redo: [] };
                entry.send.push(Number(point.logsendratekb) || 0);
                entry.redo.push(Number(point.redoratekb) || 0);
                series.set(key, entry);
            });
        }

        return rows.map((row, index) => {
            const trend = series.get(`${row.replicaid}|${row.databasename}`);
            // Preserve null: these columns are absent on the primary's own
            // rows, and zero would read as "nothing queued" instead of
            // "does not apply here".
            const num = (value: number | null | undefined) =>
                stale || value === null || value === undefined ? null : Number(value);
            const redoQueue = num(row.redoqueuekb);
            const redoRate = num(row.redoratekb);
            const lag = num(row.secondarylagseconds);
            return {
                id: index + 1,
                groupid: row.groupid,
                replicaid: row.replicaid,
                name: row.databasename,
                synchronizationstate: stale ? 'UNKNOWN' : code(SYNC_STATE, row.synchronizationstate),
                synchronizationhealth: stale ? 'UNKNOWN' : code(HEALTH, row.synchronizationhealth),
                issuspended: flag(row.issuspended),
                logsendqueuekb: num(row.logsendqueuekb),
                logsendratekbsec: num(row.logsendratekb),
                redoqueuekb: redoQueue,
                redoratekbsec: redoRate,
                ratesource: row.ratesource ?? undefined,
                // Data loss is native; recovery time is derived from the redo
                // backlog, so it is unknown whenever either input is.
                estimateddatalossseconds: lag === null ? null : Math.round(lag),
                estimatedrecoverytimeseconds:
                    redoQueue === null || redoRate === null || redoRate <= 0
                        ? null : Math.round(redoQueue / redoRate),
                logsendrateseries: trend?.send ?? [],
                redorateseries: trend?.redo ?? [],
            };
        });
    },

    getListeners: async (clusterId?: number): Promise<AvailabilityListener[]> => {
        const clusters = await topology();
        let id = 0;
        return clusters
            .filter(cluster => clusterId === undefined || cluster.id === clusterId)
            .flatMap(cluster => cluster.groups.flatMap(group =>
                group.listeners.map(listener => ({
                    id: ++id,
                    groupid: group.id,
                    dnsname: listener.dnsname,
                    port: listener.port,
                    ipaddresses: (listener.ipaddresses ?? '').split(',').filter(Boolean),
                    state: listener.state === null || listener.state === undefined
                        ? 'UNKNOWN' : (listener.state === 1 ? 'ONLINE' : 'OFFLINE'),
                }))));
    },

    // groupId narrows the result to one availability group.
    getEvents: async (clusterId: number, timeRange?: TimeRange, groupId?: number): Promise<EventsDataTable[]> => {
        if (!timeRange) return [];
        const cluster = (await topology()).find(item => item.id === clusterId);
        if (!cluster) return [];
        const groupsByDatasource = new Map<number, Set<string>>();
        cluster.groups
            .filter(group => groupId === undefined || group.id === groupId)
            .forEach(group => group.replicas.forEach(replica => {
                if (replica.instanceid == null) return;
                const names = groupsByDatasource.get(replica.instanceid) ?? new Set<string>();
                names.add(group.name);
                groupsByDatasource.set(replica.instanceid, names);
            }));
        if (groupsByDatasource.size === 0) return [];
        const scope = [...groupsByDatasource.keys()].map(id => `&datasourceid=${id}`).join('');
        // Failing softly like every other call here. A rejection would escape the
        // Promise.all the Clusters pages load with, so the page would never leave
        // its loading state over an event history that is not the point of it.
        let rows: any[] = [];
        try {
            rows = (await archiver.get<any[]>(
                `/rest/v2/event?${buildApiQuery(timeRange)}${scope}`)).data;
        } catch (error: any) {
            console.error('Failed to retrieve availability group events.', error);
            showError('Failed to retrieve availability group events',
                error.message || error.toString());
            return [];
        }
        return rows.filter(row =>
            row.object === 'availabilitygroup' &&
            groupsByDatasource.get(row.datasourceid)?.has(row.groupname)
        ).map((row, index) => toEventsDataTable(row, index));
    },
};

export default ClusterApi;
